import {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import moment from "moment";

import AttachmentsContainer from "@/components/AttachmentsContainer";
import Breadcrumbs from "@/components/Breadcrumbs";
import Button from "@mui/material/Button";
import CardsList from "@/components/CardsList";
import EmptyData from "@/components/EmptyData";
import GPSContainer from "@/components/GPSContainer";
import Loading from "@/components/Loading";
import MainActions from "@/components/MainActions";
import MatchReportsPage from "@/pages/MatchReports";
import NotesSection from "@/components/NotesSection";
import Page from "@/components/Page";
import Rating from "@/components/Rating";
import SoccerField from "@/components/SoccerField";
import Tabs from "@/components/Tabs";
import VLFCard from "@/components/Card";
import VLFModal from "@/components/Modal";
import VLFSelectNew from "@/components/Input/SelectNew";
import HandleAttendanceModal from "@/pages/Manager/Match/handleAttendance";
import HandleEventModal from "@/pages/Manager/Match/handleEvent";
import HandleReportModal from "@/pages/Manager/Match/handleReport";
import HandleRPEModal from "@/pages/Manager/Match/handleRPE";
import HandleStatModal from "@/pages/Manager/Match/handleStat";

import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import SaveIcon from "@mui/icons-material/Save";
import SwapVertIcon from "@mui/icons-material/SwapVert";

import {AttachmentAPI} from "@/api/attachment";
import {ClubAPI} from "@/api/club";
import {GpsAPI} from "@/api/gps";
import {MatchAPI} from "@/api/match";
import {PlayerAPI} from "@/api/player";
import {RosterAPI} from "@/api/roster";

import {setMatchTab} from "@/actions";

import {
	attendanceStatusOptions,
	dateFormat,
	modulesOptions,
	playerRoles,
	rpeOptions,
	ERROR_OPERATION,
	SUCCESSFUL_OPERATION,
} from "@/utils/constants";
import {getAttachment, getTextAreaField, stdev} from "@/utils/functions";
import {useCategories} from "@/utils/hooks";
import {generateMatchPDF} from "@/utils/generateMatchPDF";
import {generateMatchLineupPDF} from "@/utils/generateMatchLineupPDF";

import {
	MATCH_ADVANCED_FOR_AGAINST_SECTIONS,
	MATCH_ADVANCED_CONTEXT_SECTIONS,
	MATCH_ADVANCED_SIMPLE_SECTIONS,
	FOR_AGAINST_TYPES,
	MATCH_HALVES,
	formatAdvancedStatValue,
} from "@/config/matchAdvancedStats";

import "./index.scss";

const TeamBlockContainer = ({
	type,
	subTypes,
	title,
	withHeaderTitle = true,
	matchData,
	headerAction,
	headerActions,
	swapAction,
	deleteAction,
}) => {
	const userRole = useSelector((state) => state?.user?.roleName);

	const homeTeamPlayers = matchData?.matchPlayers?.filter(
		(mp) => mp.clubId === matchData?.homeClubId,
	);
	const awayTeamPlayers = matchData?.matchPlayers?.filter(
		(mp) => mp.clubId === matchData?.awayClubId,
	);

	const homeTeamEvents = matchData?.matchEvents?.filter(
		(me) => me.clubId === matchData?.homeClubId,
	);
	const awayTeamEvents = matchData?.matchEvents?.filter(
		(me) => me.clubId === matchData?.awayClubId,
	);

	const orderMap = playerRoles.reduce((acc, role) => {
		acc[role.value] = role.order;
		return acc;
	}, {});
	const getMatchPlayer = (p, k) => {
		return (
			<div key={k} className="match-player-container">
				<div className="j-number-container">
					<div className="number">{p.playerNumber}</div>
				</div>
				<div className="info-container d-flex flex-column">
					<div className="flex-grow-1 f-bold">
						{p.player ? p.player.firstName + " " + p.player.lastName : "---"}
					</div>
					<div className="player-role">{p.player?.primaryRole}</div>
				</div>
				<div className="y-birth">{p.playerYear}</div>
				{userRole !== "Analyst" ? (
					<div className="actions-container">
						<div className="action" onClick={() => swapAction(p)}>
							<SwapVertIcon sx={{color: "#0167c3"}} />
						</div>
						<div className="action" onClick={() => deleteAction(p)}>
							<CloseIcon />
						</div>
					</div>
				) : null}
			</div>
		);
	};
	const getMatchPlayersContainer = (c, starting = true) => {
		return (
			<div className="players-container">
				<div className="players-type">{starting ? "Titolari" : "Riserve"}</div>
				{c.players
					?.filter((p) => (starting ? p.startingPlayer : !p.startingPlayer))
					?.sort((a, b) => {
						return (
							orderMap[a.player?.primaryRole] - orderMap[b.player?.primaryRole]
						);
					})
					?.map((p, k) => {
						return getMatchPlayer(p, k);
					})}
				{!c.players?.filter((p) =>
					starting ? p.startingPlayer : !p.startingPlayer,
				)?.length ? (
					<EmptyData />
				) : null}
			</div>
		);
	};

	const getMatchEvent = (e, k, type) => {
		return (
			<div key={k} className="match-event-container">
				<div className="type-container">
					{type === "goal" && (
						<img src={require("@/assets/images/coach/football.png")} alt="" />
					)}
					{type === "sanction" && (
						<img
							src={require(`@/assets/images/coach/${e.eventType}.png`)}
							alt=""
						/>
					)}
					{type === "substitution" && <ArrowUpwardIcon />}
				</div>
				<div className="info-container d-flex flex-column">
					<div className="flex-grow-1 f-bold">
						{e.player ? e.player.firstName + " " + e.player.lastName : "---"}
					</div>
					<div className="player-role">{e.player?.primaryRole}</div>
				</div>
				{type === "substitution" && (
					<>
						<div className="type-container">
							<ArrowDownwardIcon />
						</div>
						<div className="info-container d-flex flex-column">
							<div className="flex-grow-1 f-bold">
								{e.substitutePlayer
									? e.substitutePlayer.firstName +
										" " +
										e.substitutePlayer.lastName
									: "---"}
							</div>
							<div className="player-role">
								{e.substitutePlayer?.primaryRole}
							</div>
						</div>
					</>
				)}
				<div className="time-container">
					{e.minute + "' (" + e.fraction + "T)"}
				</div>
				{userRole !== "Analyst" ? (
					<div className="actions-container">
						<div className="action" onClick={() => deleteAction(e)}>
							<CloseIcon />
						</div>
					</div>
				) : null}
			</div>
		);
	};
	const getMatchEventsContainer = (c, type) => {
		const currentTypeEvents = c.events?.filter((e) =>
			subTypes ? subTypes?.indexOf(e.eventType) !== -1 : e.eventType === type,
		);

		return (
			<div className="players-container">
				{currentTypeEvents
					?.sort((a, b) => {
						if (a.fraction !== b.fraction) {
							return a.fraction - b.fraction;
						}
						return a.minute - b.minute;
					})
					?.map((e, k) => {
						return getMatchEvent(e, k, type);
					})}
				{!currentTypeEvents?.length ? <EmptyData /> : null}
			</div>
		);
	};

	return (
		<div className="row no-margin">
			{[
				{
					clubId: matchData?.homeClubId,
					club: matchData?.homeClub,
					players: homeTeamPlayers,
					events: homeTeamEvents,
				},
				{
					clubId: matchData?.awayClubId,
					club: matchData?.awayClub,
					players: awayTeamPlayers,
					events: awayTeamEvents,
				},
			]?.map((c, k) => {
				let container = <></>;
				switch (type) {
					case "lineup":
						container = (
							<div className="block-items-container">
								{getMatchPlayersContainer(c)}
								<div className="horizontal-divider" />
								{getMatchPlayersContainer(c, false)}
							</div>
						);
						break;
					case "goal":
					case "sanction":
					case "substitution":
						container = (
							<div className="block-items-container">
								{getMatchEventsContainer(c, type)}
							</div>
						);
						break;
					default:
						break;
				}
				return (
					<div key={k} className="col-sm-12 col-lg-6 padding-small">
						{withHeaderTitle ? (
							<div className="team-block-title padding-small">{c?.club}</div>
						) : null}
						<div className="block-title-container d-flex align-items-center justify-content-between">
							<div className="block-title f-bold padding-small">{title}</div>
							<div className="d-flex align-items-center gap-2">
								{headerActions ? headerActions(c) : null}
								{userRole !== "Analyst" && headerAction ? (
									<Button
										variant="contained"
										onClick={() => headerAction(c)}
										size="small"
									>
										<div className="d-flex align-items-center">
											<AddIcon />
										</div>
									</Button>
								) : null}
							</div>
						</div>
						{container}
					</div>
				);
			})}
		</div>
	);
};

function MatchPage(props) {
	const {handleNotification} = props;

	const dispatch = useDispatch();
	const navigate = useNavigate();
	const {id} = useParams();
	const {t} = useTranslation();

	const basePath = "/matches";

	const {categories} = useCategories({
		handleNotification: undefined,
		setLoading: false,
	});

	const currentTab = useSelector((state) => state.matchTab);

	const seasonId = useSelector((state) => state?.season?.id);
	const playerId = useSelector((state) => state?.user?.playerId);

	const user = useSelector((state) => state?.user);
	const userRole = useSelector((state) => state?.user?.roleName);

	const [loading, setLoading] = useState(false);
	const [attachments, setAttachments] = useState([]);
	const [attendance, setAttendance] = useState("");
	const [clubs, setClubs] = useState([]);
	const [matchGpsData, setMatchGpsData] = useState([]);
	const [matchData, setMatchData] = useState({});
	const [matchNotes, setMatchNotes] = useState([]);
	const [prospectPlayers, setProspectPlayers] = useState([]);
	const [teamPlayers, setTeamPlayers] = useState([]);

	const [entityToDelete, setEntityToDelete] = useState({});
	const [formData, setFormData] = useState({});
	const [initialData, setInitialData] = useState();

	const [openAttendanceModal, setOpenAttendanceModal] = useState(false);
	const [openHandleConfirm, setOpenHandleConfirm] = useState(false);
	const [openEventModal, setOpenEventModal] = useState(false);
	const [openModal, setOpenModal] = useState(false);
	const [openReportModal, setOpenReportModal] = useState(false);
	const [openRPEModal, setOpenRPEModal] = useState(false);

	const [ourTacticalSystem1stHalf, setOurTacticalSystem1stHalf] = useState();
	const [opponentTacticalSystem1stHalf, setOpponentTacticalSystem1stHalf] =
		useState();
	const [ourTacticalSystem2ndHalf, setOurTacticalSystem2ndHalf] = useState();
	const [opponentTacticalSystem2ndHalf, setOpponentTacticalSystem2ndHalf] =
		useState();
	const [analysisEditing, setAnalysisEditing] = useState(false);
	const [analysisFields, setAnalysisFields] = useState({});
	const [isAnalysisSaved, setIsAnalysisSaved] = useState(false);
	const [advancedStats, setAdvancedStats] = useState(null);
	const [advancedStatsLoaded, setAdvancedStatsLoaded] = useState(false);
	const [loadingAdvancedStats, setLoadingAdvancedStats] = useState(false);

	const getMatch = async () => {
		setLoading(true);
		try {
			const response = await MatchAPI.getById(id, {seasonId});
			setMatchData(response);

			if (userRole === "Player") {
				const playerMeasurement =
					response.matchRpe?.filter((ps) => ps.playerId === playerId)?.[0] ||
					{};
				setFormData({
					...Object.assign({}, playerMeasurement),
					rpe: parseInt(playerMeasurement.rpe),
				});

				const playerConvocation = response.matchConvocations?.filter(
					(c) => c.playerId === playerId,
				)?.[0];

				setAttendance(playerConvocation ? playerConvocation.status : undefined);
				setLoading(false);
			}
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const getClubs = async () => {
		setLoading(true);
		try {
			const response = await ClubAPI.list();
			setClubs(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const getTeamPlayers = async (prospect = true) => {
		setLoading(true);
		try {
			const response = await PlayerAPI.list({prospect, seasonId});
			if (!prospect) {
				setTeamPlayers(response?.filter((p) => p.status === "Interno"));
			} else {
				setProspectPlayers(response);
			}
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const getAnalysis = async () => {
		setLoading(true);
		try {
			const response = await MatchAPI.getAnalysisById(id);
			setOurTacticalSystem1stHalf({
				value: response.ourTacticalSystem1stHalf,
				label: response.ourTacticalSystem1stHalf,
			});
			setOpponentTacticalSystem1stHalf({
				value: response.opponentTacticalSystem1stHalf,
				label: response.opponentTacticalSystem1stHalf,
			});
			setOurTacticalSystem2ndHalf({
				value: response.ourTacticalSystem2ndHalf,
				label: response.ourTacticalSystem2ndHalf,
			});
			setOpponentTacticalSystem2ndHalf({
				value: response.opponentTacticalSystem2ndHalf,
				label: response.opponentTacticalSystem2ndHalf,
			});
			setAnalysisFields(response);
			setAnalysisEditing(false);
			setIsAnalysisSaved(true);
		} catch (error) {
			if (error.response.status !== 404) {
				handleNotification(ERROR_OPERATION, "error");
			}
		} finally {
			setLoading(false);
		}
	};

	const getAdvancedStatsDetails = async () => {
		setLoadingAdvancedStats(true);
		try {
			const response = await MatchAPI.getAdvancedStats(id);
			setAdvancedStats(response);
		} catch (error) {
			if (error?.response?.status !== 404) {
				handleNotification(ERROR_OPERATION, "error");
			} else {
				setAdvancedStats(null);
			}
		} finally {
			setLoadingAdvancedStats(false);
			setAdvancedStatsLoaded(true);
		}
	};

	const getMatchAttachment = async () => {
		try {
			const response = await AttachmentAPI.list("match", id);
			setAttachments(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	const [internalTeams, setInternalTeams] = useState([]);
	const getTeams = async () => {
		try {
			const response = await RosterAPI.list({seasonId, prospect: false});
			setInternalTeams(response);
		} catch (error) {
			console.log(error);
		}
	};
	useEffect(() => {
		if (seasonId) {
			getTeams();
		}
	}, [seasonId]);

	const getGpsRows = async () => {
		setLoading(true);
		try {
			let response = await GpsAPI.list(id, {seasonId});
			setMatchGpsData(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const handleDownload = async (id) => {
		setLoading(true);
		try {
			await getAttachment(id, attachments);
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const handleAttendance = async (data, status, type) => {
		setLoading(true);
		try {
			const body = playerId
				? data
				: type === "multiple"
					? data.player?.map((p) => {
							return {
								matchId: matchData?.id,
								playerId: p.value,
								status,
							};
						})
					: {
							matchId: matchData?.id,
							playerId: data.player?.value,
							status,
						};

			delete body.player;

			type === "multiple"
				? await MatchAPI.createMultipleAttendances(body)
				: await MatchAPI.createAttendance(body);
			getMatch();
			handleClose();
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const handlePlayerAttendance = async (data, attendanceId) => {
		setLoading(true);
		try {
			attendanceId
				? await MatchAPI.updateAttendance(id, attendanceId, data)
				: await MatchAPI.createAttendance(data);
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const handleReport = async (data) => {
		setLoading(true);
		try {
			const body = data?.map((p) => {
				return {
					matchId: matchData?.id,
					clubId: initialData?.clubId,
					...p,
				};
			});

			await MatchAPI.addPlayers(body);
			getMatch();
			getTeamPlayers();
			handleClose();
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const handleEvent = async (data) => {
		setLoading(true);
		try {
			const body = {
				matchId: matchData?.id,
				clubId: initialData?.clubId,
				playerId: data.player?.value,
				playerNumber: data.player?.playerNumber,
				substitutePlayerId: data.substitutePlayer?.value,
				substitutePlayerNumber: data.substitutePlayer?.playerNumber,
				...data,
				eventType: data?.eventType?.value || initialData?.type,
			};
			delete body.player;
			await MatchAPI.addEvents(body);
			getMatch();
			handleClose();
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const handleAnalysis = async (data) => {
		setLoading(true);

		const body = {
			...analysisFields,
			ourTacticalSystem1stHalf: ourTacticalSystem1stHalf?.value,
			opponentTacticalSystem1stHalf: opponentTacticalSystem1stHalf?.value,
			ourTacticalSystem2ndHalf: ourTacticalSystem2ndHalf?.value,
			opponentTacticalSystem2ndHalf: opponentTacticalSystem2ndHalf?.value,
			matchId: id,
		};

		try {
			isAnalysisSaved
				? await MatchAPI.updateAnalysis(id, body)
				: await MatchAPI.createAnalysis(body);
			getAnalysis();
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const handleMeasurement = async (data, type, id) => {
		setLoading(true);
		try {
			if (data.rpe) {
				const rpe =
					userRole === "Player"
						? data.rpe || undefined
						: data.rpe.value || undefined;

				const playerMeasurement = playerId
					? matchData.matchRpe?.filter((ps) => ps.playerId === playerId)?.[0]
					: {};

				const body = {
					id: userRole === "Player" ? playerMeasurement?.id : id,
					matchId: matchData?.id,
					playerId: userRole === "Player" ? playerId : data.player.value,
					...data,
					rpe,
				};
				delete body.player;

				playerMeasurement?.id || id
					? await MatchAPI.updateMeasurement(
							matchData?.id,
							playerMeasurement?.id || id,
							body,
						)
					: await MatchAPI.createMeasurement(body);
			}

			if (type === "multiple") {
				const body = [];
				Object.keys(data?.players)?.forEach((pk) => {
					if (data.players[pk]?.rpe?.value) {
						body.push({
							matchId: matchData?.id,
							playerId: data.players[pk]?.player?.value,
							rpe: data.players[pk]?.rpe?.value,
						});
					}
				});

				await MatchAPI.createMeasurements(body);
			}

			if (userRole === "Player") {
				const playerConvocation = matchData.matchConvocations?.filter(
					(c) => c.playerId === playerId,
				)?.[0];

				await handlePlayerAttendance(
					{
						id: playerConvocation?.id,
						matchId: matchData?.id,
						playerId,
						status: data.rpe ? attendanceStatusOptions[0].label : attendance,
					},
					playerConvocation?.id,
				);
				navigate(basePath);
			} else {
				getMatch();
				handleClose();
			}
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const deleteMatch = async () => {
		setLoading(true);
		try {
			await MatchAPI.delete(matchData?.id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			navigate(basePath);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		}
	};

	const deleteAttachment = async () => {
		setLoading(true);
		try {
			await AttachmentAPI.delete(entityToDelete?.id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			setOpenHandleConfirm(false);
			getMatchAttachment();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const deleteAttendance = async () => {
		setLoading(true);
		try {
			await MatchAPI.deleteAttendance(matchData?.id, entityToDelete?.id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			setOpenHandleConfirm(false);
			getMatch();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const deleteMatchPlayer = async () => {
		setLoading(true);
		try {
			await MatchAPI.deletePlayer(id, entityToDelete?.id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			setOpenHandleConfirm(false);
			getMatch();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const deleteMatchEvent = async () => {
		setLoading(true);
		try {
			await MatchAPI.deleteEvent(id, entityToDelete?.id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			setOpenHandleConfirm(false);
			getMatch();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const deleteGpsRow = async () => {
		try {
			await GpsAPI.delete(matchData?.id, entityToDelete?.id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			setOpenHandleConfirm(false);
			getGpsRows();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	const deleteMeasurement = async () => {
		setLoading(true);
		try {
			await MatchAPI.deleteMeasurement(matchData?.id, entityToDelete?.id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			setOpenHandleConfirm(false);
			getMatch();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const handleClose = () => {
		setOpenModal(false);
		setOpenAttendanceModal(false);
		setOpenReportModal(false);
		setOpenEventModal(false);
		setOpenRPEModal(false);
		setInitialData();
	};

	useEffect(() => {
		getMatch();
		getMatchAttachment();
		getMatchNotes();
		setAdvancedStats(null);
		setAdvancedStatsLoaded(false);
	}, [id]);

	useEffect(() => {
		if (
			currentTab === "match_convocated_players" ||
			currentTab === "match_report_players"
		) {
			getClubs();
			getTeamPlayers(false);
		}

		if (currentTab === "match_report_players") {
			getTeamPlayers();
		}

		if (currentTab === "analysis") {
			getAnalysis();
		}

		if (currentTab === "gps") {
			getTeamPlayers();
			getGpsRows();
		}

		if (currentTab === "rpe" || currentTab === "stats") {
			getTeamPlayers(false);
			// getMatchNotes();
		}

		if (currentTab === "advanced_stats" && !advancedStatsLoaded) {
			getAdvancedStatsDetails();
		}
	}, [currentTab, advancedStatsLoaded]);

	const handleMatchPlayerUpdate = async (p) => {
		setLoading(true);
		try {
			const body = {
				...p,
				startingPlayer: !p.startingPlayer,
			};

			await MatchAPI.updatePlayer(id, p.id, body);
			getMatch();
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const getRPEAvg = () => {
		let sum = 0;
		let counter = 0;
		matchData?.matchRpe?.forEach((r) => {
			const currentPlayer = teamPlayers?.filter(
				(p) => p.id === r.playerId,
			)?.[0];

			if (currentPlayer && !currentPlayer.goalkeeper) {
				sum = sum + parseInt(r.rpe);
				counter = counter + 1;
			}
		});

		return counter > 0 ? sum / counter : 0;
	};

	// --------------------------------------------------------------------------------

	const getMatchNotes = async () => {
		try {
			const response = await MatchAPI.getNotes(id, {seasonId});
			setMatchNotes(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	const handleStat = async (data, id) => {
		setLoading(true);
		try {
			const body = {
				matchId: matchData?.id,
				...data,
				playerId: data.player?.value,
			};

			delete body.player;

			id
				? await MatchAPI.updateEvaluation(matchData?.id, id, body)
				: await MatchAPI.createEvaluation(body);
			getMatch();
			handleClose();
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const deleteEvaluation = async () => {
		setLoading(true);
		try {
			await MatchAPI.deleteEvaluation(matchData?.id, entityToDelete?.id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			setOpenHandleConfirm(false);
			getMatch();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const handleNote = async (data, id) => {
		setLoading(true);
		try {
			const body = {
				date: data?.date || moment().format("YYYY-MM-DD"),
				matchId: matchData?.id,
				...data,
			};
			delete body.player;

			id
				? await MatchAPI.updateNote(matchData?.id, id, body)
				: await MatchAPI.createNote(body);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			getMatchNotes();
			handleClose();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const deleteNote = async (id) => {
		setLoading(true);
		try {
			await MatchAPI.deleteNote(matchData?.id, id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			getMatchNotes();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const handleDownloadLineupPDF = (team) => {
		if (!matchData?.id || !team?.clubId) {
			return;
		}

		try {
			generateMatchLineupPDF(matchData, team);
			handleNotification("PDF generato con successo", "success");
		} catch (error) {
			console.error("Errore nella generazione del PDF distinta:", error);
			handleNotification("Errore nella generazione del PDF", "error");
		}
	};

	// gestire per ogni tab
	const mainActions = [
		{
			icon: <ArrowBackIcon />,
			title: "Indietro",
			onClick: () => navigate(-1),
			color: "info",
		},
	];

	if (userRole !== "Player" && matchData?.id) {
		mainActions.push({
			icon: <PictureAsPdfIcon />,
			title: "Scarica PDF",
			onClick: () => {
				try {
					generateMatchPDF(matchData, teamPlayers, t);
					handleNotification("PDF generato con successo", "success");
				} catch (error) {
					console.error("Errore nella generazione del PDF:", error);
					handleNotification("Errore nella generazione del PDF", "error");
				}
			},
		});
	}

	if (currentTab === "main_info" && userRole !== "Player") {
		mainActions.push(
			{
				icon: <CalendarMonthIcon />,
				onClick: () => navigate("/match-calendar"),
			},
			{
				icon: <EditIcon />,
				title: "Modifica",
				onClick: () => navigate(basePath + "/" + matchData?.id + "/edit"),
			},
			{
				icon: <DeleteIcon />,
				title: "Elimina",
				onClick: () => {
					setEntityToDelete({type: "match", id});
					setOpenHandleConfirm(true);
				},
				color: "error",
			},
		);
	}

	if (currentTab === "rpe" && userRole !== "Player") {
		mainActions.push({
			icon: <AddIcon />,
			onClick: () => {
				setInitialData({
					matchCategoryId: matchData?.playersCategoryId,
					matchCategoryLabel: matchData?.playersCategory,
				});
				setOpenRPEModal(true);
			},
		});
	}

	if (currentTab === "stats") {
		mainActions.push({
			icon: <AddIcon />,
			onClick: () => {
				setInitialData({
					...initialData,
					attendances: matchData?.matchConvocations,
					hasRPE: isMatchCategoryPro,
				});
				setOpenModal(true);
			},
		});
	}

	if (currentTab === "rpe" && userRole === "Player") {
		mainActions.push({
			icon: <SaveIcon />,
			title: "Salva",
			onClick: () => handleMeasurement(formData, formData?.id),
			disabled: !formData.rpe && !attendance,
		});
	}

	if (currentTab === "analysis") {
		if (!analysisEditing) {
			mainActions.push({
				icon: <EditIcon />,
				title: "Modifica",
				onClick: () => setAnalysisEditing(true),
			});
		} else {
			mainActions.push(
				{
					icon: <SaveIcon />,
					title: "Salva",
					onClick: () => handleAnalysis(),
				},
				{
					icon: <CloseIcon />,
					onClick: () => {
						setAnalysisEditing(false);
					},
					color: "error",
				},
			);
		}
	}

	if (currentTab === "advanced_stats" && userRole !== "Player") {
		mainActions.push({
			icon: <EditIcon />,
			title: "Inserisci dati",
			onClick: () => navigate(`/matches/${id}/advanced-stats`),
		});
	}

	const breadcrumbsElements = [
		{label: "Partite", path: basePath},
		{label: "/"},
	];

	const isMatchCategoryPro =
		categories?.filter((c) => c.id === matchData?.playersCategoryId)?.[0]
			?.basicActivities !== true;

	const getStatsGroups = (data, goalkeeper) => {
		if (!goalkeeper) {
			return [
				{
					title: "Stats principali",
					items: [
						{
							value: data?.goals || 0,
							label: "Goal fatti",
						},
						{
							value: data?.assists || 0,
							label: "Assist",
						},
						{
							value: data?.keyPasses || 0,
							label: "Passaggi chiave",
						},
					],
				},
			];
		}

		return [
			{
				title: "Tiri in porta",
				items: [
					{
						value: data?.saves || 0,
						label: "Parate",
					},
					{
						value: data?.goalsConceded || 0,
						label: "Goal subiti",
					},
				],
			},
			{
				title: "Letture",
				items: [
					{
						value: data?.outOfAreaReadings || 0,
						label: "Fuori area",
					},
					{
						value: data?.lowReadings || 0,
						label: "Area basse",
					},
					{
						value: data?.highReadings || 0,
						label: "Area alte",
					},
				],
			},
			{
				title: "Passaggi corti",
				items: [
					{
						value: data?.shortHorizontalPassRight || 0,
						label: "Orizz Dx",
					},
					{
						value: data?.shortHorizontalPassLeft || 0,
						label: "Orizz Sx",
					},
					{
						value: data?.shortVerticalPass || 0,
						label: "Verticale",
					},
				],
			},
			{
				// title: "Killer pass",
				items: [
					{
						value: data?.killerPass || 0,
						label: "Killer pass",
					},
				],
			},
			{
				title: "Passaggi lunghi",
				items: [
					{
						value: data?.longFrontalPass || 0,
						label: "Frontale",
					},
					{
						value: data?.longPassRight || 0,
						label: "Dx",
					},
					{
						value: data?.longPassLeft || 0,
						label: "Sx",
					},
				],
			},
		];
	};

	const getTeamPlayersConfiguration = (status) => ({
		data: !teamPlayers?.length
			? []
			: matchData?.matchConvocations
					?.filter((c) => c.status === status)
					?.map((c) => {
						const currentPlayer = teamPlayers?.find((p) => p.id === c.playerId);
						return {attendanceId: c.id, ...currentPlayer};
					})
					?.filter(Boolean)
					?.sort((a, b) => {
						return a.lastName?.localeCompare(b.lastName);
					}),
		handleDelete: (element) => {
			setEntityToDelete({
				type: "attendance",
				id: element.attendanceId,
			});
			setOpenHandleConfirm(true);
		},
	});

	const playersConvocationsTypes = [
		{label: "Convocati", status: "Presente"},
		{label: "Assenti", status: "Assente"},
		{label: "Infortunati", status: "Infortunato"},
	];

	const getTacticalSystem = (system) => {
		return system
			? {
					df: system?.value?.split("-")?.[0],
					cm: system?.value?.split("-")?.[1],
					fw: system?.value?.split("-")?.[2],
				}
			: null;
	};

	const getFractionField = (first = true) => {
		return (
			<div className="match-lineups-container">
				<div className="lineup-container">
					<div className="title">
						{matchData?.away ? matchData?.awayClub : matchData?.homeClub}
					</div>
					<VLFSelectNew
						options={modulesOptions}
						value={
							first
								? analysisEditing
									? ourTacticalSystem1stHalf
									: ourTacticalSystem1stHalf?.value
								: analysisEditing
									? ourTacticalSystem2ndHalf
									: ourTacticalSystem2ndHalf?.value
						}
						onChange={(newValue) => {
							first
								? setOurTacticalSystem1stHalf(newValue)
								: setOurTacticalSystem2ndHalf(newValue);
						}}
						label="Sistema di gioco"
						editable={analysisEditing}
					/>
					<div className="field">
						{getTextAreaField({
							values: analysisFields,
							setValues: setAnalysisFields,
							fieldKey: first ? "ourNotes1stHalf" : "ourNotes2ndHalf",
							label: "Note",
							minRows: 10,
							maxRows: 15,
							editable: analysisEditing,
						})}
					</div>
				</div>
				<div className="field-container">
					<SoccerField
						homeModule={getTacticalSystem(
							first ? ourTacticalSystem1stHalf : ourTacticalSystem2ndHalf,
						)}
						awayModule={getTacticalSystem(
							first
								? opponentTacticalSystem1stHalf
								: opponentTacticalSystem2ndHalf,
						)}
					/>
				</div>
				<div className="lineup-container">
					<div className="title">
						{matchData?.away ? matchData?.homeClub : matchData?.awayClub}
					</div>
					<VLFSelectNew
						options={modulesOptions}
						value={
							first
								? analysisEditing
									? opponentTacticalSystem1stHalf
									: opponentTacticalSystem1stHalf?.value
								: analysisEditing
									? opponentTacticalSystem2ndHalf
									: opponentTacticalSystem2ndHalf?.value
						}
						onChange={(newValue) => {
							first
								? setOpponentTacticalSystem1stHalf(newValue)
								: setOpponentTacticalSystem2ndHalf(newValue);
						}}
						label="Sistema di gioco"
						editable={analysisEditing}
					/>
					<div className="field">
						{getTextAreaField({
							values: analysisFields,
							setValues: setAnalysisFields,
							fieldKey: first ? "opponentNotes1stHalf" : "opponentNotes2ndHalf",
							label: "Note",
							minRows: 10,
							maxRows: 15,
							editable: analysisEditing,
						})}
					</div>
				</div>
			</div>
		);
	};

	const renderAdvancedStatValue = (field, metric = {}) => {
		return formatAdvancedStatValue(
			advancedStats?.[field],
			metric.decimals,
			metric.suffix,
		);
	};

	const renderForAgainstStatsCard = (section) => {
		return (
			<VLFCard
				key={section.title}
				title={section.title}
				openable={false}
				content={
					<div className="advanced-stats-card for-against">
						<div className="advanced-stats-row header">
							<div className="label">Statistica</div>
							{MATCH_HALVES.map((half, index) =>
								FOR_AGAINST_TYPES.map((type) => (
									<div
										key={`${section.title}-${half}-${type.suffix}`}
										className="cell"
									>
										{index === 0 ? "1T" : "2T"} {type.label}
									</div>
								)),
							)}
						</div>
						{section.metrics.map((metric) => (
							<div key={metric.key} className="advanced-stats-row">
								<div className="label">{metric.label}</div>
								{MATCH_HALVES.map((half) =>
									FOR_AGAINST_TYPES.map((type) => (
										<div
											key={`${metric.key}-${type.suffix}-${half}`}
											className="cell value"
										>
											{renderAdvancedStatValue(
												`${metric.key}${type.suffix}${half}`,
												metric,
											)}
										</div>
									)),
								)}
							</div>
						))}
					</div>
				}
			/>
		);
	};

	const renderContextStatsCard = (section) => {
		return (
			<VLFCard
				key={section.title}
				title={section.title}
				openable={false}
				content={
					<div className="advanced-stats-card context">
						<div className="advanced-stats-row header">
							<div className="label">Statistica</div>
							{MATCH_HALVES.map((half, index) => (
								<div key={`${section.title}-half-${half}`} className="cell">
									{index === 0 ? "1° tempo" : "2° tempo"}
								</div>
							))}
						</div>
						{section.metrics.map((metric) => (
							<div key={metric.key} className="context-group">
								<div className="group-title">{metric.label}</div>
								{metric.contexts.map((context) => (
									<div
										key={`${metric.key}-${context.suffix}`}
										className="advanced-stats-row"
									>
										<div className="label">{context.label}</div>
										{MATCH_HALVES.map((half) => (
											<div
												key={`${metric.key}-${context.suffix}-${half}`}
												className="cell value"
											>
												{renderAdvancedStatValue(
													`${metric.key}${context.suffix}${half}`,
													metric,
												)}
											</div>
										))}
									</div>
								))}
							</div>
						))}
					</div>
				}
			/>
		);
	};

	const renderSimpleStatsCard = (section) => {
		return (
			<VLFCard
				key={section.title}
				title={section.title}
				openable={false}
				content={
					<div className="advanced-stats-card simple">
						<div className="advanced-stats-row header">
							<div className="label">Statistica</div>
							{MATCH_HALVES.map((half, index) => (
								<div key={`${section.title}-simple-${half}`} className="cell">
									{index === 0 ? "1° tempo" : "2° tempo"}
								</div>
							))}
						</div>
						{section.metrics.map((metric) => (
							<div key={metric.key} className="advanced-stats-row">
								<div className="label">{metric.label}</div>
								{MATCH_HALVES.map((half) => (
									<div key={`${metric.key}-${half}`} className="cell value">
										{renderAdvancedStatValue(`${metric.key}${half}`, metric)}
									</div>
								))}
							</div>
						))}
					</div>
				}
			/>
		);
	};

	const renderAdvancedStatsTab = () => {
		if (!advancedStatsLoaded && loadingAdvancedStats) {
			return null;
		}

		if (advancedStatsLoaded && !advancedStats) {
			return (
				<div className="row no-margin g-1 advanced-stats-tab">
					<div className="col-sm-12 padding-small">
						<EmptyData />
					</div>
				</div>
			);
		}

		return (
			<div className="row no-margin g-2 advanced-stats-tab">
				<div className="col-sm-12 padding-small">
					<div className="advanced-section-title">Indicatori For/Against</div>
					<div className="advanced-section-grid">
						{MATCH_ADVANCED_FOR_AGAINST_SECTIONS.map((section) =>
							renderForAgainstStatsCard(section),
						)}
					</div>
				</div>
				<div className="col-sm-12 padding-small">
					<div className="advanced-section-title">Perdite e possesso</div>
					<div className="advanced-section-grid">
						{MATCH_ADVANCED_CONTEXT_SECTIONS.map((section) =>
							renderContextStatsCard(section),
						)}
					</div>
				</div>
				<div className="col-sm-12 padding-small">
					<div className="advanced-section-title">
						Statistiche squadra e portiere
					</div>
					<div className="advanced-section-grid">
						{MATCH_ADVANCED_SIMPLE_SECTIONS.map((section) =>
							renderSimpleStatsCard(section),
						)}
					</div>
				</div>
			</div>
		);
	};

	const tabs = [
		{
			key: "main_info",
			label: t("informations"),
			render: () => {
				return (
					<div className="row no-margin">
						<div className="col-sm-12 padding-small">
							<VLFCard
								title="Informazioni principali"
								content={
									<div className="row">
										<div className="col-sm-12">
											<div className="match-info-container">
												<div className="header-info-container">
													<div className="date-container">
														{matchData?.startDate
															? moment(matchData.startDate).format(
																	dateFormat + " HH:mm",
																)
															: null}
													</div>
													<div className="competition-container">
														{(matchData.matchType || "") +
															" " +
															(matchData.playersCategory || "")}
													</div>
												</div>
												<div className="match-body-container">
													<div className="main-info-container">
														<div className="match-teams-container">
															<div className="team-container home">
																{matchData?.homeClub}
															</div>
															<div className="teams-vs">vs</div>
															<div className="team-container away">
																{matchData?.awayClub}
															</div>
														</div>
														<div className="match-result-container">
															{Number.isInteger(matchData?.homeClubGoals)
																? matchData?.homeClubGoals +
																	" - " +
																	matchData?.awayClubGoals
																: "---"}
														</div>
													</div>
													<div className="match-details-container">
														<div className="detail-container">
															<div className="label">Durata</div>
															<div className="value">
																{moment
																	.duration(
																		moment(matchData?.endDate).diff(
																			moment(matchData?.startDate),
																		),
																	)
																	.asMinutes() + "'"}
															</div>
														</div>
														<div className="detail-container">
															<div className="label">Turno</div>
															<div className="value">
																{matchData?.round || "---"}
															</div>
														</div>
														<div className="detail-container">
															<div className="label">Stadio</div>
															<div className="value">
																{matchData?.fieldName || "---"}
															</div>
														</div>
														<div className="detail-container">
															<div className="label">Descrizione</div>
															<div className="value">
																{matchData?.description || "---"}
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>
								}
								openable={false}
							/>
						</div>
						{userRole !== "Player" && (
							<div className="col-sm-12 padding-small mt-2">
								<VLFCard
									title="Allegati"
									content={
										<AttachmentsContainer
											entityId={id}
											entityType="match"
											attachments={attachments}
											handleAdd={() => getMatchAttachment()}
											handleDownload={handleDownload}
											handleDelete={(id) => {
												setEntityToDelete({type: "attachment", id});
												setOpenHandleConfirm(true);
											}}
											handleNotification={handleNotification}
										/>
									}
								/>
							</div>
						)}
						{userRole !== "Player" && (
							<div className="col-sm-12 padding-small">
								<NotesSection
									type="match"
									notes={matchNotes}
									handleConfirm={handleNote}
									handleDelete={deleteNote}
								/>
							</div>
						)}
					</div>
				);
			},
		},
		{
			key: "match_convocated_players",
			label: t("convocations"),
			hidden: userRole === "Player" || userRole === "Analyst",
			render: () => {
				return (
					<div className="row no-margin g-1">
						{playersConvocationsTypes?.map((t, k) => {
							return (
								<div key={k} className="col-sm-12 padding-small">
									<div className="block-title-container d-flex align-items-center justify-content-between">
										<div className="block-title f-bold padding-small">
											{t.label}
										</div>
										<Button
											variant="contained"
											onClick={() => {
												setInitialData({
													matchCategoryId: matchData?.playersCategoryId,
													matchCategoryLabel: matchData?.playersCategory,
													status: t.status,
												});
												setOpenAttendanceModal(true);
											}}
											size="small"
										>
											<div className="d-flex align-items-center">
												<AddIcon />
											</div>
										</Button>
									</div>
									<div className="block-items-container">
										<CardsList
											type="roster_player"
											listConfiguration={getTeamPlayersConfiguration(t.status)}
										/>
									</div>
								</div>
							);
						})}
					</div>
				);
			},
		},
		{
			key: "match_report_players",
			label: "Distinte",
			hidden: userRole === "Player" || user?.userName === "TimossiL",
			render: () => {
				return (
					<div className="d-flex flex-column gap-3">
						<TeamBlockContainer
							type="lineup"
							title="Giocatori"
							matchData={matchData}
							headerActions={(c) => (
								<Button
									variant="outlined"
									size="small"
									onClick={() => handleDownloadLineupPDF(c)}
									startIcon={<PictureAsPdfIcon />}
								>
									PDF
								</Button>
							)}
							headerAction={(c) => {
								setInitialData({
									matchCategoryId: matchData?.playersCategoryId,
									matchCategoryLabel: matchData?.playersCategory,
									clubId: c.clubId,
									isInternal: clubs?.filter((club) => club.id === c.clubId)?.[0]
										?.internal,
								});
								setOpenReportModal(true);
							}}
							swapAction={handleMatchPlayerUpdate}
							deleteAction={(p) => {
								setEntityToDelete({type: "match_player", id: p.id});
								setOpenHandleConfirm(true);
							}}
						/>
						<TeamBlockContainer
							type="goal"
							title="Marcatori"
							withHeaderTitle={false}
							matchData={matchData}
							headerAction={(c) => {
								setInitialData({
									type: "goal",
									clubId: c.clubId,
									players: c.players, // giocatori nei titolari o riserve
								});
								setOpenEventModal(true);
							}}
							deleteAction={(e) => {
								setEntityToDelete({type: "match_event", id: e.id});
								setOpenHandleConfirm(true);
							}}
						/>
						<TeamBlockContainer
							type="sanction"
							subTypes={["yellow_card", "double_yellow_card", "red_card"]}
							title="Sanzioni"
							withHeaderTitle={false}
							matchData={matchData}
							headerAction={(c) => {
								setInitialData({
									type: "sanction",
									clubId: c.clubId,
									players: c.players, // giocatori nei titolari o riserve
								});
								setOpenEventModal(true);
							}}
							deleteAction={(e) => {
								setEntityToDelete({type: "match_event", id: e.id});
								setOpenHandleConfirm(true);
							}}
						/>
						<TeamBlockContainer
							type="substitution"
							title="Sostituzioni"
							withHeaderTitle={false}
							matchData={matchData}
							headerAction={(c) => {
								setInitialData({
									type: "substitution",
									clubId: c.clubId,
									players: c.players, // giocatori nei titolari o riserve
								});
								setOpenEventModal(true);
							}}
							deleteAction={(e) => {
								setEntityToDelete({type: "match_event", id: e.id});
								setOpenHandleConfirm(true);
							}}
						/>
					</div>
				);
			},
		},
		{
			key: "analysis",
			label: "Analisi",
			hidden:
				!isMatchCategoryPro || userRole === "Analyst" || userRole === "Player",
			render: () => {
				return (
					<div className="d-flex flex-column w-100 gap-2">
						<div className="row no-margin">
							<div className="col-sm-12 padding-small">
								<VLFCard
									title="1° tempo"
									openable={false}
									content={
										<div className="row no-margin">
											<div className="col-sm-12 padding-small">
												{getFractionField()}
											</div>
										</div>
									}
								/>
							</div>
						</div>
						<div className="row no-margin">
							<div className="col-sm-12 padding-small">
								<VLFCard
									title="2° tempo"
									openable={false}
									content={
										<div className="row no-margin">
											<div className="col-sm-12 padding-small">
												{getFractionField(false)}
											</div>
										</div>
									}
								/>
							</div>
						</div>
						<div className="row no-margin">
							<div className="col-sm-12 padding-small">
								{getTextAreaField({
									values: analysisFields,
									setValues: setAnalysisFields,
									fieldKey: "generalTeamReport",
									label: "Relazione generale squadra",
									minRows: 10,
									maxRows: 15,
									editable: analysisEditing,
								})}
							</div>
						</div>
					</div>
				);
			},
		},
		{
			key: "rpe",
			label: "RPE",
			hidden: !isMatchCategoryPro || userRole === "Analyst",
			render: () => {
				return (
					<div className="row no-margin rpe-tab-container g-2">
						{userRole === "Player" ? (
							[
								<div
									key="convocation"
									className="col-sm-12 col-lg-6 padding-small"
								>
									<div className="player-choice d-flex flex-column align-items-center justify-content-center h-100">
										<div className="choice-label">
											Hai partecipato alla partita?
										</div>
										<div className="choice-options">
											{attendanceStatusOptions.map((as, k) => {
												return (
													<div
														key={k}
														className={
															"single-option" +
															(attendance === as.value ? " selected" : "")
														}
														onClick={() => setAttendance(as.value)}
													>
														{as.label}
													</div>
												);
											})}
										</div>
									</div>
								</div>,
								<div key="rpe" className="col-sm-12 col-lg-6 padding-small">
									<div
										className="player-choice d-flex flex-column align-items-center justify-content-center h-100"
										style={{lineHeight: 1}}
									>
										<div className="choice-label">RPE</div>
										<Rating
											selectedRating={formData.rpe}
											setSelectedRating={(value) => {
												setFormData({...formData, rpe: value});
											}}
											max={10}
											min={1}
										/>
										<div>
											{
												rpeOptions.filter(
													(o) => o.value === formData.rpe?.toString(),
												)?.[0]?.label
											}
										</div>
									</div>
								</div>,
							]
						) : (
							<>
								<div className="col-sm-12 padding-small">
									<div className="rpe-team-container">
										<div className="kpi-container">
											<div className="kpi-value">{getRPEAvg()}</div>
											<div className="kpi-label">
												{"Media (portieri esclusi)"}
											</div>
										</div>
										<div className="kpi-container">
											<div className="kpi-value">
												{stdev(
													matchData?.matchRpe?.map((r) => parseInt(r.rpe)),
												)}
											</div>
											<div className="kpi-label">{"Deviazione standard"}</div>
										</div>
									</div>
								</div>
								{!matchData?.matchRpe?.length && <EmptyData />}
								{matchData?.matchRpe
									?.sort((a, b) => {
										const playerA = teamPlayers.find(
											(player) => player.id === a.playerId,
										);
										const playerB = teamPlayers.find(
											(player) => player.id === b.playerId,
										);

										const nameA = playerA?.lastName.toLowerCase();
										const nameB = playerB?.lastName.toLowerCase();

										if (nameA < nameB) return -1;
										if (nameA > nameB) return 1;
										return 0;
									})
									?.map((mr, k) => {
										const currentPlayer = teamPlayers.filter(
											(p) => p.id === mr.playerId,
										)?.[0];

										return (
											<div key={k} className="col-sm-12 padding-small">
												<div className="rpe-value-container">
													<div className="icon">
														<img
															src={require("@/assets/images/coach/rpe.png")}
															alt=""
														/>
													</div>
													<div className="player-rpe-container">
														<div className="player-info-container">
															<div className="player-container">
																<div
																	className="player clickable"
																	onClick={() =>
																		navigate("/players/" + mr.playerId)
																	}
																>
																	{currentPlayer
																		? currentPlayer.firstName +
																			" " +
																			currentPlayer.lastName
																		: "---"}
																</div>
															</div>
															<div className="player-container">
																{currentPlayer?.primaryRole}
															</div>
														</div>
														<div className="value">
															{
																rpeOptions.filter(
																	(o) => o.value === mr.rpe,
																)?.[0]?.label
															}
														</div>
													</div>
													<div className="actions-container">
														<div
															className="action"
															onClick={() => {
																setInitialData({
																	matchCategoryId: matchData?.playersCategoryId,
																	matchCategoryLabel:
																		matchData?.playersCategory,
																	...mr,
																});
																setOpenRPEModal(true);
															}}
														>
															<EditIcon />
														</div>
														<div
															className="action"
															onClick={() => {
																setEntityToDelete({
																	type: "rpe",
																	id: mr.id,
																});
																setOpenHandleConfirm(true);
															}}
														>
															<CloseIcon />
														</div>
													</div>
												</div>
											</div>
										);
									})}
							</>
						)}
					</div>
				);
			},
		},
		{
			key: "gps",
			label: "GPS",
			hidden:
				!isMatchCategoryPro || userRole === "Analyst" || userRole === "Player",
			render: () => {
				return (
					<GPSContainer
						handleNotification={handleNotification}
						gpsData={matchGpsData}
						matchId={id}
						players={teamPlayers
							?.filter(
								(p) =>
									p.goalkeeper &&
									matchData?.matchConvocations?.find(
										(mc) => mc.playerId === p.id && mc.status === "Presente",
									),
							)
							?.map((p) => {
								return {
									value: p.id,
									label: p?.firstName + " " + p?.lastName,
								};
							})}
						callback={getGpsRows}
						handleDelete={(item) => {
							setEntityToDelete({
								type: "gps",
								id: item.id,
							});
							setOpenHandleConfirm(true);
						}}
					/>
				);
			},
		},
		// {
		// 	key: "advanced_stats",
		// 	label: "Statistiche avanzate",
		// 	hidden: userRole === "Player",
		// 	render: () => renderAdvancedStatsTab(),
		// },
		{
			key: "stats",
			label: "Area dati",
			hidden: userRole === "Player",
			render: () => {
				return (
					<div className="row no-margin g-1">
						{!matchData?.matchEvaluations?.length && <EmptyData />}
						{matchData?.matchEvaluations?.map((me, k) => {
							const currentPlayer = teamPlayers.filter(
								(p) => p.id === me.playerId,
							)?.[0];
							return (
								<div
									key={k}
									className={
										isMatchCategoryPro
											? "col-sm-12 d-flex padding-small"
											: "col-sm-12 col-md-6 col-lg-3 padding-small"
									}
								>
									<div
										className={
											"match-player-stats" +
											(!isMatchCategoryPro ? " w-100" : "")
										}
									>
										<div
											className="player-name clickable"
											onClick={() => navigate("/players/" + me.playerId)}
										>
											{currentPlayer
												? currentPlayer.firstName + " " + currentPlayer.lastName
												: "---"}
										</div>
										<div className="main-stats-container">
											<div className="main-stat">
												<div className="label">Minutaggio</div>
												<span className="number">
													{me.minutes !== undefined && me.minutes !== null
														? me.minutes + "'"
														: "---"}
												</span>
											</div>
											{currentPlayer?.goalkeeper && !isMatchCategoryPro ? (
												<div className="main-stat">
													<div className="label">Goal subiti</div>
													<span className="number">
														{me.goalsConceded !== undefined &&
														me.goalsConceded !== null
															? me.goalsConceded + "'"
															: "---"}
													</span>
												</div>
											) : null}
										</div>
										<div className="d-flex align-items-center justify-content-center gap-2">
											<Button
												variant={"outlined"}
												onClick={() => {
													setInitialData({
														id: me.id,
														playerId: me.playerId,
														minutes: me.minutes,
														goalsConceded: me.goalsConceded,
														attendances: matchData?.matchConvocations,
														hasRPE: isMatchCategoryPro,
														...me,
													});
													setOpenModal(true);
												}}
												size="small"
											>
												<div className="action-content d-flex align-items-center">
													<EditIcon />
												</div>
											</Button>
											<Button
												color="error"
												variant={"outlined"}
												onClick={() => {
													setEntityToDelete({
														type: "evaluation",
														id: me.id,
													});
													setOpenHandleConfirm(true);
												}}
												size="small"
											>
												<div className="action-content d-flex align-items-center">
													<CloseIcon />
												</div>
											</Button>
										</div>
									</div>
									{isMatchCategoryPro && (
										<div className="match-stats">
											{getStatsGroups(me, currentPlayer?.goalkeeper).map(
												(sg, k) => {
													return (
														<div key={k} className="stats-group">
															<div className="group-title">{sg.title}</div>
															<div className="group-items">
																{sg.items.map((sgi, k2) => {
																	return (
																		<div key={k2} className="player-stat">
																			<div className="circle">
																				<span className="number">
																					{sgi.value}
																				</span>
																			</div>
																			<div className="label">{sgi.label}</div>
																		</div>
																	);
																})}
															</div>
														</div>
													);
												},
											)}
										</div>
									)}
								</div>
							);
						})}
						{userRole === "Admin" && isMatchCategoryPro && (
							<div className="col-sm-12 padding-small">
								<div
									className="px-1 mt-3"
									style={{fontSize: "16px", fontWeight: "bold"}}
								>
									Report gara
								</div>
								<MatchReportsPage matchId={id} />
							</div>
						)}
					</div>
				);
			},
		},
	];

	return (
		<Page className="page-container">
			<Loading visible={loading || loadingAdvancedStats} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				current={
					matchData?.startDate
						? matchData.homeClub + " - " + matchData.awayClub
						: ""
				}
				actions={<MainActions actions={mainActions} />}
			/>
			<Tabs
				tabs={tabs}
				currentTab={currentTab}
				setCurrentTab={(tab) => dispatch(setMatchTab(tab))}
			/>
			<VLFModal
				open={openHandleConfirm}
				title="Eliminazione"
				content={
					<div className="mt-2">
						Sei sicuro di voler eliminare questo elemento?
					</div>
				}
				confirm={() => {
					switch (entityToDelete?.type) {
						case "attachment":
							return deleteAttachment();
						case "attendance":
							return deleteAttendance();
						case "evaluation":
							return deleteEvaluation();
						case "gps":
							return deleteGpsRow();
						case "match":
							return deleteMatch();
						case "match_event":
							return deleteMatchEvent();
						case "match_player":
							return deleteMatchPlayer();
						case "rpe":
							return deleteMeasurement();
						default:
							return;
					}
				}}
				close={() => setOpenHandleConfirm(false)}
				confirmLabel="Conferma"
			/>
			{openModal ? (
				<HandleStatModal
					open={openModal}
					confirm={handleStat}
					close={handleClose}
					initialData={initialData}
					players={teamPlayers}
				/>
			) : null}
			{openAttendanceModal ? (
				<HandleAttendanceModal
					open={openAttendanceModal}
					confirm={handleAttendance}
					close={handleClose}
					internalTeams={internalTeams}
					players={teamPlayers?.filter(
						(p) =>
							matchData?.matchConvocations
								?.map((c) => c.playerId)
								.indexOf(p.id) === -1,
					)}
					initialData={initialData}
				/>
			) : null}
			{openEventModal ? (
				<HandleEventModal
					open={openEventModal}
					confirm={handleEvent}
					close={handleClose}
					initialData={initialData}
				/>
			) : null}
			{openReportModal ? (
				<HandleReportModal
					open={openReportModal}
					confirm={handleReport}
					close={handleClose}
					internalTeams={internalTeams}
					initialData={initialData}
					players={
						initialData?.isInternal
							? teamPlayers?.filter(
									(p) =>
										matchData?.matchConvocations?.find(
											(mc) => mc.playerId === p.id && mc.status === "Presente",
										) &&
										!matchData?.matchPlayers?.find((r) => r.playerId === p.id),
								)
							: prospectPlayers?.filter(
									(pp) =>
										!matchData?.matchPlayers?.find((r) => r.playerId === pp.id),
								)
					}
				/>
			) : null}
			{openRPEModal ? (
				<HandleRPEModal
					open={openRPEModal}
					confirm={handleMeasurement}
					close={handleClose}
					initialData={initialData}
					internalTeams={internalTeams}
					players={teamPlayers?.filter((p) => {
						return (
							initialData?.id ||
							!matchData?.matchRpe?.find((r) => r.playerId === p.id)
						);
					})}
				/>
			) : null}
		</Page>
	);
}

export default MatchPage;
